/**
 * PaymentPage Component
 * 
 * Entry point for payment method tokenization request collection.
 * Handles URL parameter parsing and auto-submission.
 * 
 * Task: T056, T067, T069, T070 [US1, US2]
 * 
 * Features:
 * - Parse email and request code from URL query params
 * - Auto-submit validation if both params present
 * - Conditional rendering: ManualEntryForm OR PaymentPortal
 * - Manual entry form integration (T067, T069)
 * - Generic error messages for validation failures (T070)
 * - Loading and error states
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEmailParam, getRequestCodeParam, getCaseIdParam } from '../utils/urlParams';
import { resolveSalesforceRequest, getErrorMessage } from '../services/api';
import PaymentPortal from '../components/PaymentPortal';
import ManualEntryForm from '../components/ManualEntryForm';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import fotlincLogo from '../lib/2025-fotlinc.svg';

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [requestId, setRequestId] = useState('');
  const [showPortal, setShowPortal] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Parse URL parameters on mount (T083 - handle partial query params)
  useEffect(() => {
    const emailParam = getEmailParam(searchParams);
    const requestCodeParam = getRequestCodeParam(searchParams);
    const caseIdParam = getCaseIdParam(searchParams);

    console.log('[PaymentPage] URL params:', { 
      email: emailParam ? 'present' : 'missing', 
      requestCode: requestCodeParam ? 'present' : 'missing',
      caseId: caseIdParam ? 'present' : 'missing'
    });

    // Salesforce flow: email + caseId present -> resolve (find-or-create) then show portal
    if (emailParam && caseIdParam) {
      const resolveCase = async () => {
        try {
          const result = await resolveSalesforceRequest({
            email: emailParam,
            caseId: caseIdParam,
          });

          setEmail(result.email || emailParam);
          setRequestId(result.requestId);
          setShowPortal(true);
          setShowManualEntry(false);
        } catch (err) {
          console.error('[PaymentPage] Salesforce resolve error:', err);
          // Generic, non-disclosing message; transient errors surface their message
          let message = getErrorMessage(err);
          if (err.response && err.response.status === 422) {
            message = 'We could not verify this request. Please check your link and try again.';
          }
          setError(message);
          setShowPortal(false);
          setShowManualEntry(false);
        } finally {
          setIsLoading(false);
        }
      };

      resolveCase();
      return;
    }

    // T083: Handle partial query params - show manual entry with pre-filled values
    if (emailParam && !requestCodeParam) {
      // Email present but no request code - show manual entry with pre-filled email
      setEmail(emailParam);
      setShowManualEntry(true);
      setShowPortal(false);
    } else if (!emailParam && requestCodeParam) {
      // Request code present but no email - show manual entry with pre-filled code
      setRequestId(requestCodeParam);
      setShowManualEntry(true);
      setShowPortal(false);
    } else if (emailParam && requestCodeParam) {
      // Both parameters present - auto-submit to PaymentPortal
      setEmail(emailParam);
      setRequestId(requestCodeParam);
      setShowPortal(true);
      setShowManualEntry(false);
    } else {
      // No parameters - show manual entry form
      setShowManualEntry(true);
      setShowPortal(false);
    }

    setIsLoading(false);
  }, [searchParams]);

  // Handle manual entry submission (T069)
  const handleManualSubmit = (submittedEmail, submittedRequestId) => {
    setIsSubmitting(true);
    setError(null);
    setEmail(submittedEmail);
    setRequestId(submittedRequestId);
    setShowManualEntry(false);
    setShowPortal(true);
    setIsSubmitting(false);
  };

  // Handle back to manual entry
  const handleBackToManualEntry = () => {
    setShowPortal(false);
    setShowManualEntry(true);
    setError(null);
    // Keep email and requestId to preserve user input
  };

  // Handle portal errors (T070 - generic error messages)
  const handlePortalError = (err) => {
    // Convert specific errors to generic messages for security
    let errorMessage = err.message;
    
    if (err.response && err.response.status === 404) {
      // Generic message - don't reveal which field is wrong (T070)
      errorMessage = 'Invalid request code or email address. Please check your details and try again.';
    } else if (err.message && err.message.includes('credentials')) {
      errorMessage = 'Invalid request code or email address. Please check your details and try again.';
    }
    
    setError(errorMessage);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fotl-gray-light">
      {/* Banner with logo matching fotlinc.com */}
      <div className="relative pt-24 pb-16">
        {/* Purple banner */}
        <div className="bg-fotl-purple h-24 w-full"></div>
        
        {/* Logo overlapping the banner - centered */}
        <div className="absolute top-8 left-0 right-0 flex justify-center">
          <div className="bg-white rounded-full p-4 shadow-lg border-4 border-fotl-purple">
            <img 
              src={fotlincLogo} 
              alt="Fruit of the Loom, Inc." 
              className="h-24 w-24"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
      {/* Error display */}
      {error && (
        <div className="max-w-md mx-auto mb-4">
          <ErrorMessage 
            message={error} 
            dismissible 
            onDismiss={() => setError(null)} 
          />
        </div>
      )}

      {/* Payment Portal (auto-submit or after manual entry) */}
      {showPortal && email && requestId && (
        <PaymentPortal
          email={email}
          requestId={requestId}
          onError={handlePortalError}
          onBack={handleBackToManualEntry}
        />
      )}

      {/* Manual Entry Form (T067, T069, T070 - User Story 2) */}
      {showManualEntry && (
        <ManualEntryForm
          onSubmit={handleManualSubmit}
          isLoading={isSubmitting}
          initialEmail={email}
          initialRequestCode={requestId}
          error={error}
        />
      )}
      </div>
    </div>
  );
};

export default PaymentPage;
